import { useEffect, useMemo, useRef, useState } from "react"
import { getFilteredTableData } from "../../../../../shared/utils/tableUtils"
import { makeStyles } from "tss-react/mui"
import {
    changeBookStatus,
    changeLockStatus,
    createProductionSlip,
    createReceipt,
    deleteOrders,
    MeetingServiceOrdersResponse,
    MerchantMeetingServiceOrdersQuery,
} from "../../../../merchant/merchant/production/meetingService/meetingServiceApi"
import { DateTime } from "luxon"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import {
    DeliveryType,
    getTimePeriod,
    MeetingServiceOrder,
    TimePeriodMeetingService,
} from "../../../../merchant/merchant/production/meetingService/meetingServiceTypes"
import { MRT_ColumnDef, MRT_RowSelectionState, MRT_TableInstance } from "material-react-table"
import { useInitialDateFilters } from "../../../../../shared/components/table/useInitialDateFilters"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Stack, Tooltip, Typography } from "@mui/material"
import { LocalizedEnum, LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useExportMeetingServiceOrdersToCsv } from "../../../../merchant/merchant/production/meetingService/components/useExportMeetingServiceOrdersToCsv"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { TableNumberOfItemsBox, TableSummaryWrap } from "../../../../../shared/components/table/TableSummaryBox"
import {
    ActionBarItem,
    ActionBarMenu,
    ActionBarMenuItem,
    FloatingActionBar,
} from "../../../../../shared/components/table/FloatingActionBar"
import { ConfirmModal } from "../../../../../shared/components/dialogs/ConfirmModal"
import {
    MeetingServiceBookDialogContent,
    MeetingServiceDeleteDialogContent,
} from "../../../../merchant/merchant/production/meetingService/components/MeetingServiceDialogContent"
import { faUser, faCircleEuro, faReceipt, faPrint, faList, faFileArrowDown } from "@fortawesome/pro-light-svg-icons"
import { faLock, faLockOpen, faCircle } from "@fortawesome/pro-solid-svg-icons"
import { fetchMeetingServiceOrders, ServiceCenterMeetingServiceOrdersQuery } from "../meetingServiceApi"
import { useAppSelector } from "../../../../store/storeHooks"
import { selectEffectiveCompanyId } from "../../../../context/contextSelectors"
import { MeetingCardMenu } from "../../../../nonAdminUser/reports/home/components/upcomingOrders/meetings/MeetingCardMenu"
import { TimeOrDatePicker } from "../../../../../shared/components/table/TimeOrDatePicker"
import { MenuMeeting } from "../../../../nonAdminUser/reports/home/weekMenuTypes"
import { useDateTime } from "../../../../localization/useDateTime"

const useStyles = makeStyles()((theme) => ({
    dateFilter: {
        maxWidth: "11rem",
    },
    deliveryTypeFilter: {
        maxWidth: "11rem",
        marginLeft: "3rem",
    },
    wrapIconText: {
        display: "flex",
        gap: "2px",
        alignItems: "center",
    },
    secondLine: {
        color: theme.palette.tailwind.neutral[500],
    },
    classNameOverride: {
        paddingTop: "4rem",
        paddingBottom: "4rem",
    },
}))

const selectMeetingService = (data: MeetingServiceOrdersResponse) => data?.orders || []

enum DialogType {
    BOOK = "book",
    DELETE = "delete",
}
enum CreatePDFType {
    SLIP = "slip",
    RECEIPT = "receipt",
}
const defaultDialogState = {
    showConfirmDialog: false,
    items: [],
    type: DialogType.BOOK,
}

export enum MEETING_SERVICE_QUERY_PARAM {
    PERIOD = "period",
    READY_FOR_INVOICING = "readyForInvoicing",
    SHOW_UNAPPROVED_ORDERS = "showUnapprovedOrders",
}

export const statusOrderList = [
    {
        id: 0,
        name: MEETING_SERVICE_QUERY_PARAM.SHOW_UNAPPROVED_ORDERS,
    },
    {
        id: 1,
        name: "isBooked",
    },
]

export type FilterOrdersProps = Readonly<{
    ordersStatus: string[]
    customersId: { customerId: string | undefined; groupId: string | undefined }[]
}>

export function MeetingService() {
    const serviceCenterId = useAppSelector(selectEffectiveCompanyId)
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()
    const todayDate = dateTimeFactory.now()

    const { l10n } = useAppLocalization()
    const [timePeriod, setTimePeriod] = useState<TimePeriodMeetingService>(TimePeriodMeetingService.TODAY)
    const [isDatePicker, setIsDatePicker] = useState<boolean>(false)
    const [deliveryType, setDeliveryType] = useState<DeliveryType>(DeliveryType.DELIVERY)
    const [cancelResourceBooking, setCancelResourceBooking] = useState<boolean>(false)

    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState<{
        startDate: DateTime
        endDate: DateTime
    }>({
        startDate: startDate,
        endDate: endDate,
    })

    const [confirmDialogState, setConfirmDialogState] = useState<{
        showConfirmDialog: boolean
        items: MeetingServiceOrder[]
        type: DialogType
    }>({ ...defaultDialogState })

    const [selectedRows, setSelectedRows] = useState<MeetingServiceOrder[]>()

    const tableInstanceRef = useRef<MRT_TableInstance<MeetingServiceOrder>>(null)

    const orderIds = selectedRows ? selectedRows.map((order) => order.id) : []

    const meetingServiceResult = useApiQuery(fetchMeetingServiceOrders, {
        queryName: ServiceCenterMeetingServiceOrdersQuery.MEETING_SERVICE,
        dependencies: {
            serviceCenterId: serviceCenterId ?? 0,
            startDate: dateState.startDate.toISODate(),
            endDate: dateState.endDate.toISODate(),
            period: isDatePicker ? undefined : timePeriod,
        },
        select: selectMeetingService,
    })

    useEffect(() => {
        if (Object.keys(rowSelection).length > 0) {
            setSelectedRows(tableInstanceRef.current?.getSelectedRowModel().rows.map((row) => row.original))
        } else {
            setSelectedRows(undefined)
        }
    }, [rowSelection])

    const { ApiQueryTable, columnFactory } = useApiQueryTable(meetingServiceResult)

    const { mutate: callChangeBookStatus } = useApiMutation(changeBookStatus, {
        async onSuccess(_params, _data, context) {
            await context.queryClient.invalidateQueries({
                queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE],
            })
        },
    })

    const { mutate: callChangeLockStatus } = useApiMutation(changeLockStatus, {
        async onSuccess(_params, _data, context) {
            await context.queryClient.invalidateQueries({
                queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE],
            })
        },
    })

    const { mutateAsync: callCreateProductionSlip } = useApiMutation(createProductionSlip, {})
    const { mutateAsync: callCreateReceipt } = useApiMutation(createReceipt, {})
    const handlePDFOpenInNewTab = async (type: CreatePDFType, orderIdsList: number[]) => {
        const response =
            type === CreatePDFType.SLIP
                ? await callCreateProductionSlip({ orderIds: orderIdsList })
                : await callCreateReceipt({ orderIds: orderIdsList })
        const url = window.URL.createObjectURL(response)
        window.open(url, "_blank")
        window.URL.revokeObjectURL(url)
    }

    const { mutate: callDeleteOrders, isLoading: isDeletingOrders } = useApiMutation(deleteOrders, {
        async onSuccess(_params, _data, context) {
            await context.queryClient.invalidateQueries({
                queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE],
            })
            handleClearSelected()
        },
    })

    const columns = useMemo<MRT_ColumnDef<MeetingServiceOrder>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("meeting-service-table-order-id-column"),
                accessorKey: "id",
            }),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("meeting-service-table-delivery-time-column"),
                    accessorKey: "deliveryDetails.time",
                },
                (row) => row?.deliveryDetails?.time,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("meeting-service-table-meeting-name-column"),
                accessorKey: "deliveryDetails",
                Cell: ({ row }) => {
                    const { id, meetingDetails, deliveryDetails, paymentDetails, customer } = row.original

                    return (
                        <Grid container gap={1.25}>
                            <Grid container alignItems="center" gap={2} flexWrap="nowrap">
                                <LocalizedStrict
                                    id="meeting-service-table-meeting"
                                    vars={{ locationName: meetingDetails.meetingName }}
                                >
                                    <Typography variant="body2">Meeting: {meetingDetails.meetingName}</Typography>
                                </LocalizedStrict>
                                <Typography variant="body2" className={classes.wrapIconText}>
                                    <FontAwesomeIcon icon={faUser} color={"#334155"} />
                                    {deliveryDetails.sittings}
                                </Typography>
                                {paymentDetails.isContantPayment && (
                                    <FontAwesomeIcon icon={faCircleEuro} color={"#334155"} />
                                )}
                                <IconButton onClick={() => callChangeLockStatus({ orderIds: [id] })} size="small">
                                    {deliveryDetails.isLocked ? (
                                        <LocalizedStrict
                                            id="meeting-service-table-lock-column-tooltip-closed"
                                            attrs={{ title: true }}
                                        >
                                            <Tooltip arrow title="The order is locked for changes">
                                                <FontAwesomeIcon icon={faLock} color={"#334155"} />
                                            </Tooltip>
                                        </LocalizedStrict>
                                    ) : (
                                        <LocalizedStrict
                                            id="meeting-service-table-lock-column-tooltip-open"
                                            attrs={{ title: true }}
                                        >
                                            <Tooltip arrow title="The order is locked for changes">
                                                <FontAwesomeIcon icon={faLockOpen} color={"#22C55E"} />
                                            </Tooltip>
                                        </LocalizedStrict>
                                    )}
                                </IconButton>
                            </Grid>
                            <Grid
                                container
                                alignItems="center"
                                gap={1.5}
                                flexWrap="nowrap"
                                className={classes.secondLine}
                            >
                                <LocalizedEnum base="delivery-type-filter" enumValue={deliveryDetails.type}>
                                    <Typography variant="caption">{deliveryDetails.type}</Typography>
                                </LocalizedEnum>
                                <FontAwesomeIcon icon={faCircle} color={"#94A3B8"} width="5px" />
                                <Typography variant="caption">{meetingDetails.meetingName}</Typography>
                                <FontAwesomeIcon icon={faCircle} color={"#94A3B8"} width="5px" />
                                <Typography variant="caption">{customer.displayName}</Typography>
                            </Grid>
                        </Grid>
                    )
                },
            }),
            columnFactory.nameColumn({
                header: l10n.getString("meeting-service-table-delivery-location-column"),
                accessorKey: "deliveryDetails.location.name",
            }),
        ],
        [l10n, columnFactory],
    )

    const handleChangeTimeOrDatePicker = () => {
        setIsDatePicker((prev) => !prev)
    }
    const handleChangeTimePeriod = (value: any) => {
        setTimePeriod(value)
    }
    const handleChangeDeliveryType = (event: any) => {
        setDeliveryType(event.target.value)
    }

    const exportOrders = useExportMeetingServiceOrdersToCsv({
        data: selectedRows,
        startDate: isDatePicker
            ? (dateState.startDate ?? todayDate)
            : getTimePeriod(dateTimeFactory, timePeriod)?.start,
        endDate: dateState.endDate ?? todayDate,
    })

    const handleClearSelected = () => {
        setSelectedRows(undefined)
        setRowSelection({})
    }

    const mapMeetingServiceToMeeting = (booking: MeetingServiceOrder): MenuMeeting => {
        const { meetingName, startDateTime, endDateTime } = booking.meetingDetails

        return {
            id: booking.id,
            uid: booking.uid,
            orderSummary: booking.orderSummary,
            organizers: booking.organizers,
            meetingDetails: {
                meetingName,
                startDateTime,
                endDateTime,
            },
        }
    }

    return (
        <Stack gap="2rem">
            <ApiQueryTable
                tableInstanceRef={tableInstanceRef}
                enableRowSelection
                columns={columns}
                onRowSelectionChange={setRowSelection}
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <MeetingCardMenu
                        ignoreMenuContainer
                        menuOnApi
                        meetings={getFilteredTableData(tableInstanceRef)}
                        meeting={mapMeetingServiceToMeeting(row.original)}
                        handleMenuClose={closeMenu}
                    />,
                ]}
                renderTopToolbarCustomActions={() => (
                    <Grid container gap={1} flexWrap="nowrap" paddingLeft="1.5rem" paddingTop="1rem">
                        <TimeOrDatePicker<TimePeriodMeetingService>
                            isDatePicker={isDatePicker}
                            dateState={dateState}
                            setDateState={setDateState}
                            timePeriod={timePeriod}
                            handleChangeTimePeriod={handleChangeTimePeriod}
                            handleChangeTimeOrDatePicker={handleChangeTimeOrDatePicker}
                        />
                        <FormControl fullWidth className={classes.deliveryTypeFilter}>
                            <InputLabel id="time-period-select-label">
                                <LocalizedStrict id="meeting-service-table-delivery-type">
                                    <>Delivery type</>
                                </LocalizedStrict>
                            </InputLabel>
                            <Select
                                labelId="time-period-select-label"
                                id="time-period-select"
                                value={deliveryType}
                                label="Delivery type"
                                onChange={handleChangeDeliveryType}
                            >
                                {Object.entries(DeliveryType).map(([key, value]) => (
                                    <MenuItem key={key} value={value}>
                                        <LocalizedEnum base="delivery-type-filter" enumValue={value}>
                                            <span>{value}</span>
                                        </LocalizedEnum>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                )}
                renderToolbarInternalActions={({ table }) => <StandardTableFilter variant="right" table={table} />}
                renderBottomToolbarCustomActions={({ table }) => (
                    <TableSummaryWrap>
                        <LocalizedStrict id="table-summary-box-number-of-orders" attrs={{ title: true }}>
                            <TableNumberOfItemsBox
                                data={table.getFilteredRowModel().rows}
                                isLoading={meetingServiceResult.isLoading}
                                title="Number of orders"
                            />
                        </LocalizedStrict>
                    </TableSummaryWrap>
                )}
                state={{
                    rowSelection,
                }}
            />
            {selectedRows && (
                <FloatingActionBar selectedItemsLength={selectedRows.length} handleCloseActionBar={handleClearSelected}>
                    <LocalizedStrict id="meeting-service-table-floating-action-bar-print" attrs={{ title: true }}>
                        <ActionBarMenu icon={faPrint} title="Print">
                            <LocalizedStrict
                                id="meeting-service-table-floating-action-bar-production-slip"
                                attrs={{ title: true }}
                            >
                                <ActionBarMenuItem
                                    icon={faList}
                                    title="Production slip"
                                    onClick={() => handlePDFOpenInNewTab(CreatePDFType.SLIP, orderIds)}
                                />
                            </LocalizedStrict>
                            <LocalizedStrict
                                id="meeting-service-table-floating-action-bar-receipt"
                                attrs={{ title: true }}
                            >
                                <ActionBarMenuItem
                                    icon={faReceipt}
                                    title="Receipt"
                                    onClick={() => handlePDFOpenInNewTab(CreatePDFType.RECEIPT, orderIds)}
                                />
                            </LocalizedStrict>
                        </ActionBarMenu>
                    </LocalizedStrict>

                    <LocalizedStrict id="meeting-service-table-floating-action-bar-export" attrs={{ title: true }}>
                        <ActionBarItem
                            icon={faFileArrowDown}
                            title="Export"
                            onClick={() => {
                                exportOrders()
                            }}
                        />
                    </LocalizedStrict>
                </FloatingActionBar>
            )}
            {confirmDialogState.showConfirmDialog &&
                (confirmDialogState.type === DialogType.DELETE ? (
                    <ConfirmModal
                        open={confirmDialogState.showConfirmDialog}
                        title={l10n.getString("meeting-service-delete-orders-title")}
                        buttonLabelSubmit={l10n.getString("meeting-service-delete-orders-button")}
                        buttonSubmitLoading={isDeletingOrders}
                        handleClick={() => {
                            const ids = confirmDialogState.items.map((order) => order.id)
                            callDeleteOrders({ orderIds: ids, cancelResourceBooking, cancelMeetingService: true })
                        }}
                        onClose={() => setConfirmDialogState(defaultDialogState)}
                        maxWidthProp="lg"
                        classNameOverride={classes.classNameOverride}
                    >
                        <MeetingServiceDeleteDialogContent
                            list={confirmDialogState.items}
                            checked={cancelResourceBooking}
                            handleChange={() => setCancelResourceBooking((prev) => !prev)}
                        />
                    </ConfirmModal>
                ) : (
                    <ConfirmModal
                        open={confirmDialogState.showConfirmDialog}
                        title={
                            <LocalizedStrict id="meeting-service-book-orders-title">
                                <span>Book orders</span>
                            </LocalizedStrict>
                        }
                        buttonLabelSubmit={l10n.getString("meeting-service-book-orders-button")}
                        handleClick={() => {
                            const list = confirmDialogState.items.map((order) => {
                                return {
                                    id: order.id,
                                    ...(order.id ? { cashPaymentDate: "2024-02-22" } : {}),
                                }
                            })
                            callChangeBookStatus({ orderIds: list })
                        }}
                        onClose={() => setConfirmDialogState(defaultDialogState)}
                        maxWidthProp="lg"
                        classNameOverride={classes.classNameOverride}
                    >
                        <MeetingServiceBookDialogContent list={confirmDialogState.items} />
                    </ConfirmModal>
                ))}
        </Stack>
    )
}
